import React from 'react';
import { formatNumber, getPriceChangeDetails } from '@/utils/coins/formatNumber';
import { CoinProps } from '@/types/coins';
import { ChartSquare, MoneyChange, Ranking, DollarCircle } from 'iconsax-react';
import { formatTomanPrice } from '@/utils/coins/bubble';
import { isUsdQuoted } from '@/utils/coins/quote';

const SingleCoinIntro: React.FC<CoinProps> = ({
    change,
    marketCap,
    name,
    nameFa,
    price,
    rank,
    dominance,
    symbol,
    volume24h,
    typeId,
    price_usd,
    quote_currency,
}) => {
    const { text, color, onlyText } = getPriceChangeDetails(change || 0);

    // Gold Intro Layout
    if (typeId === 2) {
        const tomanPrice =
            typeof price === 'number'
                ? price
                : typeof price === 'object'
                  ? price?.ir_price_buy || 0
                  : 0;
        const usdQuoted = isUsdQuoted({ price_usd, quote_currency });
        return (
            <div className='flex flex-col gap-4 mb-4'>
                <div className='bg-amber-500/5 dark:bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 text-sm text-justify leading-relaxed dark:text-gray-200'>
                    <p className='mb-2'>
                        <span className='font-bold text-amber-600 dark:text-amber-400'>
                            {nameFa || name}
                        </span>{' '}
                        {usdQuoted
                            ? 'مرجع جهانی قیمت طلا است و قیمت طلا و سکه داخلی از آن اثر می‌گیرد. قیمت لحظه‌ای'
                            : 'یکی از ابزارهای شاخص بازار طلا و سکه ایران است. قیمت لحظه‌ای'}{' '}
                        <span className='font-bold'>{nameFa || name}</span> امروز{' '}
                        {usdQuoted && (
                            <>
                                <span className='font-bold text-emerald-600 dark:text-emerald-400 mx-1'>
                                    <bdi dir='ltr'>
                                        ${formatNumber(price_usd || 0, false, true, 'USD', true)}
                                    </bdi>
                                </span>{' '}
                                دلار معادل{' '}
                            </>
                        )}
                        <span className='font-bold text-emerald-600 dark:text-emerald-400 mx-1'>
                            <bdi dir='ltr'>{formatTomanPrice(tomanPrice, symbol)}</bdi>
                        </span>{' '}
                        تومان می‌باشد.
                    </p>
                    <p>
                        قیمت <span className='font-bold'>{nameFa || name}</span> در ۲۴ ساعت اخیر{' '}
                        <span className='font-bold mx-1' style={{ color: color }}>
                            {text ? `${text} ${onlyText}` : onlyText}
                        </span>{' '}
                        داشته است. نمودار قیمت لحظه‌ای و تحلیل تکنیکال برای بررسی روند بازار در
                        ادامه قرار داده شده است.
                    </p>
                </div>
            </div>
        );
    }

    // Currency Intro Layout
    if (typeId === 3) {
        const tomanPrice =
            typeof price === 'number'
                ? price
                : typeof price === 'object'
                  ? price?.ir_price_buy || 0
                  : 0;
        return (
            <div className='flex flex-col gap-4 mb-4'>
                <div className='bg-blue-500/5 dark:bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4 text-sm text-justify leading-relaxed dark:text-gray-200'>
                    <p className='mb-2'>
                        <span className='font-bold text-blue-600 dark:text-blue-400'>
                            {nameFa || name}
                        </span>{' '}
                        یکی از ارزهای رسمی موجود در بازار آزاد ارز کشور است. قیمت لحظه‌ای{' '}
                        <span className='font-bold'>{nameFa || name}</span> امروز{' '}
                        <span className='font-bold text-emerald-600 dark:text-emerald-400 mx-1'>
                            <bdi dir='ltr'>{formatTomanPrice(tomanPrice, symbol)}</bdi>
                        </span>{' '}
                        تومان می‌باشد.
                    </p>
                    <p>
                        قیمت <span className='font-bold'>{nameFa || name}</span> در ۲۴ ساعت اخیر{' '}
                        <span className='font-bold mx-1' style={{ color: color }}>
                            {text ? `${text} ${onlyText}` : onlyText}
                        </span>{' '}
                        داشته است.
                    </p>
                </div>
            </div>
        );
    }

    // Crypto Layout with Quick Stat Grid Cards
    return (
        <div className='flex flex-col gap-6 mb-6'>
            {/* Stat Cards Grid */}
            <div className='grid grid-cols-2 sm:grid-cols-4 gap-3'>
                <div className='bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3 rounded-2xl flex flex-col justify-between transition-transform hover:-translate-y-0.5 duration-200'>
                    <div className='flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1'>
                        <span>ارزش بازار (Cap)</span>
                        <ChartSquare size='16' className='text-blue-500' />
                    </div>
                    <div className='text-base font-bold dark:text-white' dir='ltr'>
                        {formatNumber(marketCap || 0, true, true, 'USD', true)}
                    </div>
                </div>

                <div className='bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3 rounded-2xl flex flex-col justify-between transition-transform hover:-translate-y-0.5 duration-200'>
                    <div className='flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1'>
                        <span>حجم معاملات ۲۴h</span>
                        <MoneyChange size='16' className='text-emerald-500' />
                    </div>
                    <div className='text-base font-bold dark:text-white' dir='ltr'>
                        {formatNumber(volume24h || 0, true, true, 'USD', true)}
                    </div>
                </div>

                <div className='bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3 rounded-2xl flex flex-col justify-between transition-transform hover:-translate-y-0.5 duration-200'>
                    <div className='flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1'>
                        <span>رتبه کریپتو</span>
                        <Ranking size='16' className='text-amber-500' />
                    </div>
                    <div className='text-base font-bold dark:text-white'>#{rank || '-'}</div>
                </div>

                <div className='bg-gray-50 dark:bg-white/5 border border-gray-100 dark:border-white/10 p-3 rounded-2xl flex flex-col justify-between transition-transform hover:-translate-y-0.5 duration-200'>
                    <div className='flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1'>
                        <span>سهم از بازار (Dominance)</span>
                        <DollarCircle size='16' className='text-purple-500' />
                    </div>
                    <div className='text-base font-bold dark:text-white' dir='ltr'>
                        %{dominance ? dominance.toFixed(2) : '0.00'}
                    </div>
                </div>
            </div>

            {/* Formatted Text Container */}
            <div className='bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30 rounded-2xl p-4 text-sm text-justify leading-relaxed dark:text-gray-200'>
                <p className='mb-2'>
                    <span className='font-bold text-blue-600 dark:text-blue-400'>
                        {nameFa || name}
                    </span>{' '}
                    با نماد اختصاری{' '}
                    <span className='font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300'>
                        {symbol}
                    </span>{' '}
                    یکی از ارزهای دیجیتال اصلی بازار است که با ارزش بازار{' '}
                    <span dir='ltr' className='font-bold inline-block'>
                        ${formatNumber(marketCap || 0, true, true, 'USD', true)}
                    </span>{' '}
                    در رتبه <span className='font-bold'>{rank}</span> قرار دارد. قیمت لحظه‌ای{' '}
                    <span className='font-bold'>{nameFa || name}</span> امروز{' '}
                    <span dir='ltr' className='font-bold inline-block'>
                        ${formatNumber(price?.price_buy || 0, false, true, 'USD', true)}
                    </span>{' '}
                    دلار (معادل{' '}
                    <span className='font-bold text-emerald-600 dark:text-emerald-400'>
                        {formatNumber(price?.ir_price_buy || 0, false, true, 'TMN', true)} تومان
                    </span>
                    ) می‌باشد.
                </p>
                <p>
                    تغییر قیمت <span className='font-bold'>{nameFa || name}</span> در ۲۴ ساعت گذشته{' '}
                    <span className='font-bold mx-1' style={{ color: color }}>
                        {text ? `${text} ${onlyText}` : onlyText}
                    </span>{' '}
                    بوده و حجم معاملات ۲۴ ساعته آن به دلار معادل{' '}
                    <span dir='ltr' className='font-bold inline-block'>
                        ${formatNumber(volume24h || 0, false, true, 'USD', true)}
                    </span>{' '}
                    است.
                </p>
            </div>
        </div>
    );
};

export default SingleCoinIntro;
